import { z } from "zod";

/**
 * まとめてメールの送信待ち（#268 1.5c-2、docs/98 §7）。全社の送る速さ（MAIL_RATE_LIMIT）・会社の1日の上限で止まった残りを積み、
 * API の見張り（mail/queue.ts）が枠の空いたときに1通ずつ、1枚ずつ送るのと同じ口で送る。送信予約は作らない。
 */
export const MAIL_QUEUE_LIMITS = {
  /** 見張りが見る間（ミリ秒） */
  everyMs: 30_000,
  /** 1回に取る数 */
  perTick: 5,
  /** 速さの上限で止まったとき、次に試すまで（分） */
  rateRetryMin: 3,
  /** 1日の上限で止まったとき：日本時間の翌日 0:00 の何分後 */
  dayRetryMin: 5,
  /** 送信の不調（503）で試し直すまで（分）と、送れなかったにする回数 */
  failRetryMin: 5,
  maxAttempts: 5,
} as const;

export const MailQueueStatus = z.enum(["queued", "sending", "sent", "failed", "canceled"]);
export type MailQueueStatus = z.infer<typeof MailQueueStatus>;
export const MAIL_QUEUE_STATUS_LABEL: Record<MailQueueStatus, string> = { queued: "送信待ち", sending: "送信中", sent: "送信済", failed: "送信失敗", canceled: "取消" };

export const MailQueueItem = z.object({
  id: z.string().uuid(),
  documentId: z.string().uuid(),
  documentNo: z.string().nullable(),
  clientName: z.string(),
  status: MailQueueStatus,
  reason: z.enum(["rate", "day"]),
  attempts: z.number().int(),
  nextTryAt: z.string(),
  lastCode: z.string().nullable(),
  deliveryId: z.string().uuid().nullable(),
  createdAt: z.string(),
  finishedAt: z.string().nullable(),
});
export type MailQueueItem = z.infer<typeof MailQueueItem>;
export const MailQueueList = z.object({ items: z.array(MailQueueItem) });
export type MailQueueList = z.infer<typeof MailQueueList>;

/** 日本時間の翌日 0:00 ＋ n 分（1日の上限で止まったときの次に試す時刻） */
export function nextJstDay(now: Date, plusMin: number): Date {
  const jst = new Date(now.getTime() + 9 * 3600 * 1000);
  const next = Date.UTC(jst.getUTCFullYear(), jst.getUTCMonth(), jst.getUTCDate() + 1) - 9 * 3600 * 1000;
  return new Date(next + plusMin * 60_000);
}

export const MAIL_QUEUE_MSG = {
  title: "送信待ち",
  help: "まとめてメールで、送る速さの上限か1日の上限に達して送れなかった請求書です。枠が空き次第、1枚ずつ送るときと同じ宛先・文面で1通ずつ送ります。送ったものは送付の記録に入ります",
  colAt: "登録日時",
  colInvoice: "請求書",
  colClient: "{customer}",
  colStatus: "状態",
  colNext: "次の送信",
  empty: "送信待ちはありません",
  toList: "送信待ち一覧へ",
  open: "表示",
  reasonRate: "送る速さの上限",
  reasonDay: "1日の上限",
  cancel: "取消",
  canceled: "送信待ちを取り消しました",
  notFound: "送信待ちが見つかりません",
  notQueued: "送信待ちのものだけ取り消せます",
  failedCode: (code: string): string =>
    code === "MAIL_NO_TO" ? "宛先がありません"
    : code === "MAIL_COMPANY_REQUIRED" ? "会社名がありません"
    : code === "MAIL_QUEUE_USER_GONE" || code === "MAIL_QUEUE_NO_USER" ? "依頼した利用者が利用できません"
    : `送れませんでした（${code}）`,
} as const;
